import { useEffect, useRef, useState } from 'react';
import { loopSpeedRamp } from '@shared/progression';
import { clampPlaybackRate } from '../../game/audio/scheduler';
import { loopHypeMessage } from './loopHypeMessages';

export interface LoopSpeedupBannerState {
  /** Effective (audio-clamped) playback multiplier for this loop, e.g. 1.5. */
  speed: number;
  /** Rotating hype line for this loop. */
  message: string;
}

/**
 * loopIndex 0 = first play; loopIndex 1 = first repeat, which is bridged by
 * the dedicated "INFINITE MODE" finale overlay. From loopIndex 2 on, a
 * repeat otherwise drops straight into a bare canvas count-in — that's where
 * this banner steps in to announce the new speed.
 */
const FIRST_BANNER_LOOP_INDEX = 2;

/**
 * Drives the {@link LoopSpeedupBanner} shown over the count-in on each
 * infinite-mode repeat (loop 3+). Returns the banner content while the
 * session is counting in such a loop, or `null` otherwise. Shared across
 * every looping play page so all modes behave identically.
 *
 * @param isLeadIn  Whether the session is in its `lead_in` (count-in) phase.
 *                  Passed as a plain boolean so the hook stays decoupled
 *                  from each mode's bespoke session-status union.
 * @param getLoopIndex  Reads the session's current 0-based loop index. Only
 *                  invoked when the count-in begins, so a fresh closure each
 *                  render is fine (captured via a ref).
 */
export function useLoopSpeedupBanner(
  isLeadIn: boolean,
  getLoopIndex: () => number,
): LoopSpeedupBannerState | null {
  const getLoopIndexRef = useRef(getLoopIndex);
  getLoopIndexRef.current = getLoopIndex;
  const [banner, setBanner] = useState<LoopSpeedupBannerState | null>(null);

  useEffect(() => {
    if (!isLeadIn) {
      setBanner(null);
      return;
    }
    const loopIndex = getLoopIndexRef.current();
    if (loopIndex < FIRST_BANNER_LOOP_INDEX) {
      setBanner(null);
      return;
    }
    setBanner({
      speed: clampPlaybackRate(loopSpeedRamp(loopIndex).startSpeed),
      message: loopHypeMessage(loopIndex),
    });
  }, [isLeadIn]);

  return banner;
}
